import { Box, Button, Group, Text } from '@mantine/core';
import type { Zone } from '@seo/contracts';
import { publicMessageOf } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from '../pages/EntityPage.module.css';

/**
 * Картка рядка реєстру: редагування й видалення того, що вже існує.
 *
 * Дзеркало `CreatePanel`, і зʼявилось із тієї ж причини — але на протилежному боці. Реєстри
 * були **write-once**: `create` існував із першого дня, а `update` і `delete` не існували
 * навіть у API. Помилка в назві юрисдикції лишалась назавжди, а рядок, заведений помилково, —
 * теж. Бекенд отримав дванадцять процедур 14.08; це екран до них.
 *
 * Три речі, які тут не випадкові:
 *
 * - **Панель, а не модалка.** У застосунку немає інфраструктури модалок, і всі наявні форми
 *   інлайнові. Один патерн на всі реєстри краще за шість різних.
 * - **Видалення підтверджується вголос, у два кроки.** Не `window.confirm`: він не
 *   тестується, не стилізується й не каже, ЩО саме зникне. Тут перший клік перетворює
 *   кнопку на питання з іменем рядка, другий — виконує.
 * - **Право на видалення — окремий рівень матриці.** У ній `content` має `write`, але не
 *   `delete`; кнопка питає матрицю сама, тож жоден екран не може про це забути.
 */
export interface RowEditorProps {
  /** Що редагуємо — показується в заголовку й у питанні про видалення. */
  title: string;
  /** Префікс `data-testid`: `license` → `edit-license-panel`, `-save`, `-delete`, `-error`. */
  testId: string;
  zone: Zone;
  onClose: () => void;
  busy?: boolean;
  error?: unknown;
  onSubmit: (event: React.SyntheticEvent<HTMLFormElement>) => void;
  /** Відсутній ⇒ рядок не видаляється (наприклад, поки на нього хтось посилається). */
  onDelete?: () => void;
  deleting?: boolean;
  deleteError?: unknown;
  /** Питання, яке бачить оператор перед видаленням. За замовчуванням — назва рядка. */
  deletePrompt?: string;
  confirmingDelete: boolean;
  onConfirmingDeleteChange: (value: boolean) => void;
  children: React.ReactNode;
}

export function RowEditor({
  title,
  testId,
  zone,
  onClose,
  busy = false,
  error,
  onSubmit,
  onDelete,
  deleting = false,
  deleteError,
  deletePrompt,
  confirmingDelete,
  onConfirmingDeleteChange,
  children,
}: RowEditorProps): React.JSX.Element {
  const canWrite = useCan(zone, 'write');
  const canDelete = useCan(zone, 'delete');

  return (
    <Box className={classes.panel} data-testid={`edit-${testId}-panel`}>
      <Group justify="space-between" mb={8}>
        <Text className={classes.panelTitle}>{title}</Text>
        <Button
          size="compact-xs"
          variant="subtle"
          data-testid={`edit-${testId}-close`}
          onClick={onClose}
        >
          Закрити
        </Button>
      </Group>

      <form onSubmit={onSubmit} data-testid={`edit-${testId}-form`} noValidate>
        <Group gap="sm" align="flex-start" wrap="wrap">
          {children}
          {/*
           * Без права на запис поля лишаються видимими, а кнопки немає: картка й далі
           * відповідає на питання «що там записано», і лише не обіцяє це змінити.
           */}
          {canWrite ? (
            <Button type="submit" size="xs" loading={busy} data-testid={`edit-${testId}-save`}>
              Зберегти
            </Button>
          ) : null}
        </Group>

        {error == null ? null : (
          <Text className={classes.danger} mt={6} data-testid={`edit-${testId}-error`} role="alert">
            {publicMessageOf(error)}
          </Text>
        )}
      </form>

      {onDelete === undefined || !canDelete ? null : (
        <Group gap="sm" mt="md">
          {confirmingDelete ? (
            <>
              <Text className={classes.warn} data-testid={`edit-${testId}-confirm`}>
                {deletePrompt ?? `Видалити «${title}» назавжди?`}
              </Text>
              <Button
                size="compact-xs"
                color="rose"
                loading={deleting}
                data-testid={`edit-${testId}-delete-confirm`}
                onClick={onDelete}
              >
                Так, видалити
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                data-testid={`edit-${testId}-delete-cancel`}
                onClick={() => {
                  onConfirmingDeleteChange(false);
                }}
              >
                Скасувати
              </Button>
            </>
          ) : (
            <Button
              size="compact-xs"
              variant="default"
              data-testid={`edit-${testId}-delete`}
              onClick={() => {
                onConfirmingDeleteChange(true);
              }}
            >
              Видалити
            </Button>
          )}
        </Group>
      )}

      {deleteError == null ? null : (
        <Text
          className={classes.danger}
          mt={6}
          data-testid={`edit-${testId}-delete-error`}
          role="alert"
        >
          {publicMessageOf(deleteError)}
        </Text>
      )}
    </Box>
  );
}
